import type { Session, UserData, UUID } from '@cobliteam/common-types';
import { create } from 'zustand';
import { devtools } from 'zustand/middleware';

export interface AuthState {
  session: Session | null;
  /** `userId` dono do `userData` em memória — evita usar dados de outro usuário após troca de sessão. */
  userId: UUID | null;
  userData: UserData | null;

  setSession: (session: Session) => void;
  updateSession: (patch: Partial<Session>) => void;
  clearSession: () => void;

  setUserData: (userId: UUID, userData: UserData) => void;
  clearUserData: () => void;

  /** Reset atômico usado por `useLogout` — limpa session e userData num único write. */
  reset: () => void;
}

type AuthData = Pick<AuthState, 'session' | 'userId' | 'userData'>;

const initialState: AuthData = {
  session: null,
  userId: null,
  userData: null,
};

// O boot otimista persistia session/userData nesta chave (revertido — o store voltou a ser
// somente em memória). Remove o snapshot morto dos browsers no primeiro load pós-deploy.
const LEGACY_AUTH_STORAGE_KEY = 'cobli-auth';
if (typeof window !== 'undefined') {
  try {
    window.localStorage.removeItem(LEGACY_AUTH_STORAGE_KEY);
  } catch {
    // localStorage indisponível (modo privado etc.) — nada a fazer.
  }
}

/**
 * Fonte única para estado de autenticação em memória (session + userData). Substituiu
 * `useSessionStore` + `useUserDataStore`. Tokens continuam em `libs/api-lib` (AuthToken.*).
 *
 * Sem persistência em localStorage: a cada boot a sessão é revalidada contra `/account/session`
 * e o `userData` buscado do servidor (hooks em `modules/hooks/src/useSession/` e
 * `useEntity/useUser/useUserData`), com os providers mostrando loading até lá.
 */
const useAuthStore = create<AuthState, [['zustand/devtools', never]]>(
  devtools(
    (set) => ({
      ...initialState,
      setSession: (session) => set({ session }, false, 'auth/session/set'),
      updateSession: (patch) => set((state) => ({ session: state.session ? { ...state.session, ...patch } : state.session }), false, 'auth/session/update'),
      clearSession: () => set({ session: null }, false, 'auth/session/clear'),
      setUserData: (userId, userData) => set({ userId, userData }, false, 'auth/userData/set'),
      clearUserData: () => set({ userId: null, userData: null }, false, 'auth/userData/clear'),
      reset: () => set(initialState, false, 'auth/reset'),
    }),
    { name: 'useAuthStore' }
  )
);

export default useAuthStore;
